import { Plus } from 'lucide-react';
import type { OptionalAddition } from '../../agent/agent-api';
import { Button } from '../../components/Button/Button';
import styles from './ProposalAdditions.module.css';

export interface ProposalAdditionsProps {
  items: OptionalAddition[];
  /**
   * Picker mode: each element becomes a row with an "Add" action. Toggled by the
   * card's "Add detail" control. Without it the section is purely informational.
   */
  editing?: boolean;
  /** Called with the element the user chose to add (picker mode only). */
  onAdd?: (item: OptionalAddition) => void;
  /** Disable the picker while a turn is streaming. */
  disabled?: boolean;
}

/**
 * The "optional additions" section on a create proposal card: the elements the
 * agent could add but the user hasn't set (a bundle's base price, a client's
 * contacts…), each with a plain-language hint. By default it is informational —
 * it makes the richer surface visible at review time. In picker mode (the card's
 * "Add detail" control) each element gets an "Add" action so the user can pick
 * one; the card then withdraws the proposal and asks the agent to re-propose it
 * with that element included. Rendered inside both the plain proposal card and
 * the rich catalog artifact card, so it's the single source of this section's look.
 */
export function ProposalAdditions({
  items,
  editing = false,
  onAdd,
  disabled = false,
}: ProposalAdditionsProps) {
  if (items.length === 0) return null;
  const interactive = editing && !!onAdd;

  if (interactive) {
    return (
      <div className={styles.Additions}>
        <div className={styles.Head}>
          <Plus aria-hidden="true" />
          What would you like to add?
        </div>
        <div className={styles.Options}>
          {items.map((item) => (
            <div key={item.label} className={styles.OptionRow}>
              <div className={styles.OptionText}>
                <span className={styles.OptionLabel}>{item.label}</span>
                <span className={styles.OptionHint}>{item.hint}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={styles.Add}
                disabled={disabled}
                onClick={() => onAdd?.(item)}
              >
                Add
              </Button>
            </div>
          ))}
        </div>
        <p className={styles.Note}>Pick one and I will ask for anything I need.</p>
      </div>
    );
  }

  return (
    <div className={styles.Additions}>
      <div className={styles.Head}>
        <Plus aria-hidden="true" />
        Optional — not set yet
      </div>
      <dl className={styles.List}>
        {items.map((item) => (
          <div key={item.label} className={styles.Row}>
            <dt className={styles.Label}>{item.label}</dt>
            <dd className={styles.Hint}>{item.hint}</dd>
          </div>
        ))}
      </dl>
      <p className={styles.Note}>Use the Add detail button to include any of these before creating it.</p>
    </div>
  );
}
